/**
 * shell-plugin — the studio shell: the SPA's index route.
 *
 * The studio's index route (`/`) is the application's LANDING/home surface (#164):
 * the main page of the whole app. `shellPlugin` registers its OWN landing editor
 * (`component/landing`, a `React.lazy` split chunk like `DevCanvas`/`IdeShell` so
 * it stays out of the app entry — gate 8) and routes `/` to it. The landing lists
 * the app's surfaces and offers a PRIMARY "Open a project / Enter the IDE" action
 * that routes into a host's IDE (`/ide`, or via the #148 picker the nested
 * `/{hostId}/projects/{projectSlug}` deep-link). It LINKS to the IDE; it does NOT
 * embed it.
 *
 * The genome-dev-plugin harness — which `/` previously borrowed as a bootstrap
 * dogfood — stays reachable at `/dev` (devPlugin owns that route). The harness
 * canvas target is still exported (`HARNESS_CANVAS_TARGET`) for the tests that
 * pin the `/dev` reachability.
 *
 * @module @ubiclouds/v5-studio/genome/shell-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The dev-plugin's canvas editor target (its `.editor('canvas')` component). */
export const HARNESS_CANVAS_TARGET = 'v5-genome-dev-plugin:component/canvas';

/** The studio shell plugin id. */
export const STUDIO_SHELL_ID = 'v5-studio-shell';

/**
 * The shell's OWN landing editor target (same-namespace). `/` routes to this; the
 * `routesTo: 'component/landing'` short form auto-expands to this fully-qualified
 * id, which the tests pin.
 */
export const LANDING_TARGET = `${STUDIO_SHELL_ID}:component/landing`;

// The landing surface is `React.lazy` so Vite splits it (and the workspace
// contracts the picker drags) out of the app entry chunk (gate 8).
const LazyLanding = lazy(() => import('./LandingPage.js'));

/**
 * The studio shell plugin — registers the landing editor and routes the SPA index
 * `/` to it (`routesTo: 'component/landing'` → this plugin's `:component/landing`
 * entity; resolved by `resolveRoutes`, gate 4).
 */
export const shellPlugin: ComposableGenomePlugin = definePlugin(STUDIO_SHELL_ID)
  .name('Studio Shell')
  .trustTier('core')
  .editor('landing', LazyLanding)
  .route('/', { routesTo: 'component/landing', title: 'Studio' })
  .build();
